import * as React from 'react'
import * as Redux from 'react-redux'
import styled, { css } from 'styled-components'

import * as date from '@owl-nest/date'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'
import * as hooks from '@owl-nest/hooks'

import * as projectModel from '../../../../models/project'
import { RootState } from '../../../../../reducers'

import { Diptych } from './Diptych'

export function DateInfo(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const endDate = model.project.hadExtraTime<projectModel.ExtraFields>(project)
    ? date.parseISO(project.date_end_extra_time ?? '')
    : project.date_end && date.parseISO(project.date_end)
  const startDate = project.date_start && date.parseISO(project.date_start)

  const [isTooltipOpen, setToolTipOpen] = React.useState(false)

  const eventHandlers = {
    onClick: () => {},
    onMouseEnter: () => {
      setToolTipOpen(true)
    },
    onMouseLeave: () => {
      setToolTipOpen(false)
    },
    onTouchStart: () => {
      setToolTipOpen(!isTooltipOpen)
    },
  }

  if (!startDate || !endDate) {
    const remainingDays = endDate || !project.nb_days ? '—' : project.nb_days
    console.warn('Project is missing end date and/or start date.', { endDate, startDate })
    return (
      <Diptych
        handlers={eventHandlers}
        leftZone={
          <>
            {endDate && (
              <plume.styles.tooltip.BlockAnchor>
                <plume.styles.tooltip.Tooltip open={isTooltipOpen} fitToContent>
                  <plume.styles.copy.S.regular>
                    {t('Planned end date: %(date)s', { date: date.format(endDate, 'PPP') })}
                  </plume.styles.copy.S.regular>
                </plume.styles.tooltip.Tooltip>
              </plume.styles.tooltip.BlockAnchor>
            )}
          </>
        }
        rightZone={
          <Content $column={false}>
            <plume.styles.heading.XS>{remainingDays}</plume.styles.heading.XS>
            <plume.styles.copy.S.regular>&nbsp;{t('days left')}</plume.styles.copy.S.regular>
          </Content>
        }
      />
    )
  }

  const formattedEndDate = date.format(endDate, 'PPP')
  const formattedEndTime = date.format(endDate, 'p')
  const formattedStartDate = date.format(startDate, 'PPP')

  const remainingTime = hooks.useDateCountdown(endDate, { reloadOnEnd: true })

  const isFinished = model.project.isFinished<projectModel.ExtraFields>(project)

  return (
    <DiptychStyled
      $extraTime={project.is_in_extra_time}
      handlers={eventHandlers}
      leftZone={
        <>
          <plume.styles.tooltip.BlockAnchor>
            <plume.styles.tooltip.Tooltip fitToContent open={isTooltipOpen}>
              <plume.styles.copy.S.regular>
                {t('Launched on %(date)s', { date: formattedStartDate })}
                <br />
                {endDate &&
                  (project.finished
                    ? t('Campaign ended %(date)s', { date: formattedEndDate })
                    : t('Ends %(date)s at %(time)s', { date: formattedEndDate, time: formattedEndTime }))}
              </plume.styles.copy.S.regular>
            </plume.styles.tooltip.Tooltip>
            <>{getPicto()}</>
          </plume.styles.tooltip.BlockAnchor>
        </>
      }
      rightZone={
        <>
          {project.finished ? (
            <Content $column={true}>
              <plume.styles.copy.S.regular>
                {project.is_in_extra_time ? (
                  <strong>{t('Extension!')} </strong>
                ) : (
                  <>
                    <strong>{t('Campaign ended')} </strong>
                    <plume.styles.copy.S.regular>{getDescription()}</plume.styles.copy.S.regular>
                  </>
                )}
              </plume.styles.copy.S.regular>
            </Content>
          ) : (
            <Content $column={false}>
              <plume.styles.heading.XS>{remainingTime.value}</plume.styles.heading.XS>
              <plume.styles.copy.S.regular>&nbsp;{getDescription()}</plume.styles.copy.S.regular>
            </Content>
          )}
        </>
      }
    />
  )

  function getPicto(): React.ReactElement<unknown> {
    if (project.is_in_extra_time) {
      return <plume.glyphs.twoToned.StopwatchPlus size={34} />
    } else if (!isFinished && remainingTime.endingSoon) {
      return <plume.glyphs.twoToned.Stopwatch size={34} />
    }
    return <plume.glyphs.twoToned.Calendar size={34} />
  }

  function getDescription(): string {
    if (!project.is_in_extra_time) {
      if (isFinished && endDate) {
        return getRelativeEndDate(endDate)
      } else {
        return remainingTime.label
      }
    } else {
      return ''
    }
  }
}

function getRelativeEndDate(endDate: Date | number): string {
  const differenceInCalendarDays = date.differenceInCalendarDays(endDate, Date.now())
  if (differenceInCalendarDays === -1) {
    return t('yesterday')
  } else if (differenceInCalendarDays === 0) {
    return t('today')
  }

  const formattedEndDate = date.format(endDate, 'PPP')
  return t('the %(date)s', { date: formattedEndDate })
}

const DiptychStyled = styled(Diptych)<{ $extraTime: boolean }>`
  ${({ $extraTime = false }) => {
    if ($extraTime) {
      return css`
        align-items: center;
      `
    }
  }}

  /* NOT SCREEN */
  @media not screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.styles.tooltip.Tooltip} {
      left: 130px;
    }
  }
`

const Content = styled.div<{ $column: boolean }>`
  display: flex;
  align-items: baseline;
  margin-left: 6px;

  ${({ $column = false }) => {
    if ($column) {
      return css`
        flex-direction: column;
      `
    }
  }}
`
